import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  PieChart,
  Pie,
  Cell,
  Legend,
  Tooltip,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as LineTooltip,
  ReferenceLine,
  ResponsiveContainer,
} from "recharts";
import { KPI_METRICS } from "../../constants/cfoDashBoard";
import {
  apiFetchOutStandingRewardPointsBal,
  apiFetchWagesPayable,
  apiFetchFieldLeaderWages,
  apiFetchAmbassadorWages,
} from "../../api/backend/cfo";
import { ImgTopBtn, GlassBtn } from "../../components/buttons";
import {
  LineIntersectingCircleIcon,
  LineChartReportIcon,
  LineChartIcon,
  FilterIcon,
} from "../../components/icons";
import DatePicker from "../../components/datePicker/datePicker";
import "./cfoDashboard.scss";

export default function CfoDashboard() {
  const navigate = useNavigate();
  // Date filter state
  const startOfYear = new Date(new Date().getFullYear(), 0, 1);
  const [startDateValue, setStartDateValue] = useState(startOfYear);
  const [endDateValue, setEndDateValue] = useState(new Date());
  const [fromDate, setFromDate] = useState(startOfYear.toISOString());
  const [toDate, setToDate] = useState(new Date().toISOString());
  const [isDatePickerOpen, setDatePickerOpen] = useState(false);
  const [dateOrderError, setDateOrderError] = useState(false);

  // Raw and computed data
  const [view, setView] = useState("capTable");
  // Raw data including manual inputs and dynamic fields
  const initialRawData = {
    currentQuarterRevenue: 150000,
    priorQuarterRevenue: 130000,
    currentMRR: 50000,
    churnRate: 5,
    lifetimeValue: 600,
    cac: 120,
    cashBalance: 50000,
    monthlyBurnRate: 30000,
    currentAssets: 221000,
    currentLiabilities: 60000,
    accountsPayable: 25000,
    wagesPayableYTD: 0,
    wagesPayableFiltered: 0,
    fieldLeaderWagesFiltered: 0,
    ambassadorWagesFiltered: 0,
    travelExpenses: 3000,
    legalFees: 5000,
    rewardPointsSum: 0,
    mrrHistory: [
      { month: "Jan", mrr: 45000, churnRate: 4.5 },
      { month: "Feb", mrr: 46000, churnRate: 4.7 },
      { month: "Mar", mrr: 47000, churnRate: 4.9 },
      { month: "Apr", mrr: 48000, churnRate: 5.1 },
    ],
    shareholders: [
      { name: "Founder & President", shares: 10000000, classType: "Class B" },
      { name: "Unissued Shares", shares: 5000000, classType: "Class A" },
      { name: "Preferred", shares: 5000000, classType: "Preferred" },
    ],
    totalShares: 20000000,
    proForma: [
      {
        year: 2025,
        revenue: 800000,
        accountsPayable: 60000,
        wagesPayable: 50000,
        travelExpenses: 30000,
        legalFees: 20000,
        serverCost: 10000,
        hostingCost: 8000,
        ambassadorCost: 20000,
        attorneyFees: 12000,
      },
      {
        year: 2026,
        revenue: 1200000,
        accountsPayable: 80000,
        wagesPayable: 60000,
        travelExpenses: 35000,
        legalFees: 25000,
        serverCost: 12000,
        hostingCost: 10000,
        ambassadorCost: 30000,
        attorneyFees: 15000,
      },
      {
        year: 2027,
        revenue: 1600000,
        accountsPayable: 100000,
        wagesPayable: 70000,
        travelExpenses: 40000,
        legalFees: 30000,
        serverCost: 15000,
        hostingCost: 12000,
        ambassadorCost: 40000,
        attorneyFees: 18000,
      },
    ],
  };
  const [rawData, setRawData] = useState(initialRawData);
  const [metrics, setMetrics] = useState({});
  const [error, setError] = useState("");

  // fetch reward‐points
  useEffect(() => {
    apiFetchOutStandingRewardPointsBal()
      .then((res) => {
        const pts = Number(res.data.rewardPointsSum) || 0;
        setRawData((prev) => ({ ...prev, rewardPointsSum: pts }));
      })
      .catch((err) => {
        console.error(err);
        setError(err.message || "Failed to load data");
      });
  }, []);

  useEffect(() => {
    apiFetchWagesPayable({ from: fromDate, to: toDate })
      .then((res) => {
        setRawData((prev) => ({
          ...prev,
          wagesPayableFiltered: res.data.total ?? 0,
        }));
      })
      .catch((err) => setError(err.message));
  }, [fromDate, toDate]);

  // Callbacks invoked only when buttons are pressed
  const handleFetchFieldLeaderWages = () => {
    apiFetchFieldLeaderWages({ from: fromDate, to: toDate })
      .then((res) => {
        setRawData((prev) => ({
          ...prev,
          fieldLeaderWagesFiltered: res.data.total ?? 0,
        }));
      })
      .catch((err) => setError(err.message));
  };

  const handleFetchAmbassadorWages = () => {
    apiFetchAmbassadorWages({ from: fromDate, to: toDate })
      .then((res) => {
        setRawData((prev) => ({
          ...prev,
          ambassadorWagesFiltered: res.data.total ?? 0,
        }));
      })
      .catch((err) => setError(err.message));
  };

  // compute KPI metrics
  useEffect(() => {
    const computed = {};
    KPI_METRICS.forEach((metric) => {
      const inputs = metric.formulaKeys.map((k) => rawData[k] || 0);
      let val = 0;
      switch (metric.id) {
        case "revenueGrowth": {
          const [curr, prior] = inputs;
          val = prior > 0 ? ((curr - prior) / prior) * 100 : 0;
          break;
        }
        case "grossProfitMargin": {
          const [rev, cogs] = inputs;
          val = rev > 0 ? ((rev - cogs) / rev) * 100 : 0;
          break;
        }
        case "ebitdaMargin": {
          const [ebitda, rev] = inputs;
          val = rev > 0 ? (ebitda / rev) * 100 : 0;
          break;
        }
        case "cashRunway": {
          const [cash, burn] = inputs;
          val = burn > 0 ? cash / burn : 0;
          break;
        }
        default:
          val = inputs.reduce((s, x) => s + x, 0);
      }
      computed[metric.id] = val;
    });
    setMetrics(computed);
  }, [rawData]);

  if (error) return <p style={{ color: "red" }}>{error}</p>;

  // cap‐table rows
  const shareRows = [
    ...rawData.shareholders.map((sh, i) => {
      const pct = rawData.totalShares
        ? (sh.shares / rawData.totalShares) * 100
        : 0;
      return (
        <tr key={i}>
          <td>{sh.name}</td>
          <td>{sh.shares.toLocaleString()}</td>
          <td>{sh.classType}</td>
          <td>{pct.toFixed(2)}%</td>
        </tr>
      );
    }),
    <tr key="total">
      <td>
        <strong>Total</strong>
      </td>
      <td>{rawData.totalShares.toLocaleString()}</td>
      <td></td>
      <td>100.00%</td>
    </tr>,
  ];

  // pro‐forma rows
  const proFormaRows = rawData.proForma.map((pf) => {
    const totalExp =
      pf.accountsPayable +
      pf.wagesPayable +
      pf.travelExpenses +
      pf.legalFees +
      pf.serverCost +
      pf.hostingCost +
      pf.ambassadorCost +
      pf.attorneyFees;
    const net = pf.revenue - totalExp;
    return (
      <tr key={pf.year}>
        <td>{pf.year}</td>
        <td>${pf.revenue.toLocaleString()}</td>
        <td>${pf.accountsPayable.toLocaleString()}</td>
        <td>${pf.wagesPayable.toLocaleString()}</td>
        <td>${pf.travelExpenses.toLocaleString()}</td>
        <td>${pf.legalFees.toLocaleString()}</td>
        <td>${pf.serverCost.toLocaleString()}</td>
        <td>${pf.hostingCost.toLocaleString()}</td>
        <td>${pf.ambassadorCost.toLocaleString()}</td>
        <td>${pf.attorneyFees.toLocaleString()}</td>
        <td>
          <strong>${net.toLocaleString()}</strong>
        </td>
      </tr>
    );
  });

  // format options for display
  const dateDisplayOptions = { year: "numeric", month: "long", day: "numeric" };

  // donut data + colors
  const pieData = [
    { name: "Current Assets", value: rawData.currentAssets || 0 },
    { name: "Current Liabilities", value: rawData.currentLiabilities || 0 },
    { name: "Working Capital", value: metrics.workingCapital || 0 },
    { name: "Accounts Payable Paid", value: metrics.accountsPayable || 0 },
    { name: "Total Wages Payable Paid", value: metrics.wagesPayable || 0 },
    {
      name: "Field Leader & Ambassador Total Wages Paid",
      value: rawData.wagesPayableFiltered ?? 0,
    },
    {
      name: "Field Leader Wages Paid",
      value: rawData.fieldLeaderWagesFiltered ?? 0,
    },
    {
      name: "Ambassador Wages Paid",
      value: rawData.ambassadorWagesFiltered ?? 0,
    },
    { name: "Travel Expenses", value: metrics.travelExpenses || 0 },
    { name: "Lawyers Fees", value: metrics.legalFees || 0 },
    { name: "Reward-Points Liability", value: rawData.rewardPointsSum || 0 },
  ];
  const COLORS = [
    "var(--color-darkGold)",
    "var(--color-corveteRed)",
    "var(--color-lightGreen)",
    "var(--color-yellowgold)",
    "var(--color-bloodred)",
    "var(--color-gum)",
    "var(--color-yellowgold)",
    "var(--color-bloodred)",
  ];

  // DatePicker handlers
  const handleStartDateChange = (date) => {
    if (!date) return clearStartDate();
    const iso = date.toISOString();
    setStartDateValue(date);
    setFromDate(iso);
    localStorage.setItem("cfo_startDate", iso);
  };
  const handleEndDateChange = (date) => {
    if (!date) return clearEndDate();
    const iso = date.toISOString();
    setEndDateValue(date);
    setToDate(iso);
    localStorage.setItem("cfo_endDate", iso);
  };
  const clearStartDate = () => {
    localStorage.removeItem("cfo_startDate");
    setStartDateValue(null);
    setFromDate(startOfYear.toISOString());
  };
  const clearEndDate = () => {
    localStorage.removeItem("cfo_endDate");
    setEndDateValue(null);
    setToDate(new Date().toISOString());
  };
  const handleDateErrorChange = (hasError) => {
    console.log("Date order error status:", hasError);
    setDateOrderError(hasError);
  };

  return (
    <div className="cfoDashboard">
      {/* header */}
      <div className="header">
        <h1>CFO Dashboard</h1>
        <ImgTopBtn
          image={<LineIntersectingCircleIcon width="25" height="25" />}
          imgTopBtnWidth="50%"
          imgTopBtnTextFontSize="1.3rem"
          imgTopBtnFontColor="#b03a2e"
          imgTopBtnText="Logout"
          onClick={() => {
            localStorage.clear();
            navigate("/", { replace: true });
          }}
        />
      </div>
      {/* donut */}
      <div className="chartWrapper">
        <ResponsiveContainer>
          <PieChart>
            <Pie
              data={pieData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius={60}
              outerRadius={100}
              labelLine={false}
              label={false}
            >
              {pieData.map((_, i) => (
                <Cell key={i} fill={COLORS[i % COLORS.length]} />
              ))}
            </Pie>
            <Legend
              verticalAlign="bottom"
              formatter={(label, entry, i) =>
                `${label} – $${(pieData[i].value ?? 0).toLocaleString()}`
              }
            />

            <Tooltip formatter={(v) => v.toLocaleString()} />
          </PieChart>
        </ResponsiveContainer>
      </div>
      {/* date filter */}
      <div className="dateFilter">
        <div className="periodDisplay">
          Wages Payable FilterPeriod:{" "}
          {startDateValue
            ? startDateValue.toLocaleDateString(undefined, dateDisplayOptions)
            : "--"}{" "}
          –{" "}
          {endDateValue
            ? endDateValue.toLocaleDateString(undefined, dateDisplayOptions)
            : "--"}
        </div>

        <DatePicker
          onClick={() => setDatePickerOpen(true)}
          onStartDateChange={handleStartDateChange}
          onEndDateChange={handleEndDateChange}
          clearStartDate={clearStartDate}
          clearEndDate={clearEndDate}
          setStartDateValue={setStartDateValue}
          setEndDateValue={setEndDateValue}
          startDateValue={startDateValue}
          endDateValue={endDateValue}
          onDateErrorChange={handleDateErrorChange}
          showAdditionalDate={false}
          isClearable={true}
          showYearDropdown={true}
          showMonthDropdown={true}
          numYears={10}
          dateFormat="MMMM d, yyyy"
          open={isDatePickerOpen}
        />
      </div>
      <div className="toggleBtns">
        <ImgTopBtn
          image={
            <FilterIcon
              width="25"
              height="25"
              fillColor="var(--color-bloodred)"
            />
          }
          imgTopBtnWidth="50%"
          imgTopBtnTextFontSize="1.3rem"
          imgTopBtnFontColor="var(--color-bloodred)"
          imgTopBtnText="Filter Field Leader Wages"
          onClick={handleFetchFieldLeaderWages}
        />
        <ImgTopBtn
          image={
            <FilterIcon
              width="25"
              height="25"
              fillColor="var(--color-bloodred)"
            />
          }
          imgTopBtnWidth="50%"
          imgTopBtnTextFontSize="1.3rem"
          imgTopBtnFontColor="var(--color-bloodred)"
          imgTopBtnText="Filter Ambassador Wages"
          onClick={handleFetchAmbassadorWages}
        />
      </div>
      {/* MRR / churn trend */}
      <div className="chartWrapper">
        <ResponsiveContainer>
          <LineChart data={rawData.mrrHistory}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis yAxisId="left" />
            <YAxis yAxisId="right" orientation="right" unit="%" />
            <LineTooltip />
            <Legend formatter={(n) => (n === "mrr" ? "MRR" : "Churn Rate")} />
            <ReferenceLine yAxisId="left" y={0} stroke="#000" />
            <Line
              yAxisId="left"
              dataKey="mrr"
              stroke="var(--color-yellow)"
              dot={false}
            />
            <Line
              yAxisId="right"
              dataKey="churnRate"
              stroke="var(--color-darkGold)"
              dot={{ r: 4 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* toggle & tables */}
      <div className="toggleBtns">
        <GlassBtn
          glassBtnImage={
            <LineChartIcon
              height="15px"
              width="15px"
              fillColor={
                view === "capTable"
                  ? "var(--color-offWhite)"
                  : "var(--color-yellow)"
              }
            />
          }
          glassHoverStyle={{
            height: "55px",
          }}
          className="glass-btn"
          glassRefClassName="adsManagerGlassBtnRef"
          glassBtnText="Cap Table"
          glassBtnHeight="20px"
          glassBtnWidth="150px"
          glassBtnTextFontSize="1rem"
          glassBtnFontWeight="900"
          glassBtnFontColor={
            view === "capTable"
              ? "var(--color-offWhite)"
              : "var(--color-yellow)"
          }
          glassBtnTextDecorationColor={
            view === "capTable"
              ? "var(--color-offWhite)"
              : "var(--color-yellow)"
          }
          onClick={() => setView("capTable")}
          hi2Style={{
            transform: "rotate(90deg) translate(-60px, 75px)",
            zIndex: 9,
          }}
          hi3Style={{
            transform: "rotate(90deg) translate(-60px, -75px)",
            zIndex: 9,
          }}
        />
        <GlassBtn
          glassBtnImage={
            <LineChartReportIcon
              height="15px"
              width="15px"
              fillColor={
                view === "proForma"
                  ? "var(--color-offWhite)"
                  : "var(--color-yellow)"
              }
            />
          }
          className="glass-btn"
          glassHoverStyle={{
            height: "55px",
          }}
          glassBtnText="Pro Forma"
          glassBtnHeight="20px"
          glassBtnWidth="150px"
          glassBtnTextFontSize="1rem"
          glassBtnFontWeight="900"
          glassBtnFontColor={
            view === "proForma"
              ? "var(--color-offWhite)"
              : "var(--color-yellow)"
          }
          glassBtnTextDecorationColor={
            view === "proForma"
              ? "var(--color-offWhite)"
              : "var(--color-yellow)"
          }
          onClick={() => setView("proForma")}
          hi2Style={{
            transform: "rotate(90deg) translate(-60px, 75px)",
            zIndex: 9,
          }}
          hi3Style={{
            transform: "rotate(90deg) translate(-60px, -75px)",
            zIndex: 9,
          }}
        />
      </div>

      {view === "capTable" && (
        <table className="dataTable">
          <thead>
            <tr>
              <th>Name</th>
              <th>Shares</th>
              <th>Class</th>
              <th>Ownership</th>
            </tr>
          </thead>
          <tbody>{shareRows}</tbody>
        </table>
      )}

      {view === "proForma" && (
        <>
          <h2>Pro Forma Projections</h2>
          <table className="dataTable">
            <thead>
              <tr>
                <th>Year</th>
                <th>Revenue</th>
                <th>AP</th>
                <th>Wages</th>
                <th>Travel</th>
                <th>Legal</th>
                <th>Server</th>
                <th>Hosting</th>
                <th>Ambassador</th>
                <th>Attorney</th>
                <th>Net Profit</th>
              </tr>
            </thead>
            <tbody>{proFormaRows}</tbody>
          </table>
        </>
      )}
      {/* KPI grid */}
      <div className="header">
        <h1>KPI</h1>
      </div>
      <div className="kpiGrid ">
        {KPI_METRICS.map((metric) => {
          const val = metrics[metric.id];
          if (val == null) return null;
          let disp = "";
          switch (metric.unit) {
            case "%":
              disp = `${val.toFixed(2)}%`;
              break;
            case "$":
              disp = `$${Math.round(val).toLocaleString()}`;
              break;
            case "days":
              disp = `days ${Math.round(val).toLocaleString()}`;
              break;
            case "x":
              disp = `x${val.toLocaleString()}`;
              break;
            default:
              disp = `${metric.unit}${val.toLocaleString()}`;
          }
          return (
            <div key={metric.id} className="kpiCard">
              <div className="kpiLabel">{metric.label}</div>
              <div className="kpiValue">{disp}</div>
            </div>
          );
        })}
        <div className="kpiCard">
          <div className="kpiLabel">Outstanding Reward Points</div>
          <div className="kpiValue">
            {rawData.rewardPointsSum.toLocaleString()}
          </div>
        </div>
        <div className="kpiCard">
          <div className="kpiLabel">
            Total Field Leader & Ambassador Wages Payable
          </div>
          <div className="kpiValue">
            ${(rawData.wagesPayableFiltered ?? 0).toLocaleString()}
          </div>
        </div>
        <div className="kpiCard">
          <div className="kpiLabel">Field Leader Wages</div>
          <div className="kpiValue">
            ${(rawData.fieldLeaderWagesFiltered ?? 0).toLocaleString()}
          </div>
        </div>
        <div className="kpiCard">
          <div className="kpiLabel">Ambassador Wages</div>
          <div className="kpiValue">
            ${(rawData.ambassadorWagesFiltered ?? 0).toLocaleString()}
          </div>
        </div>
      </div>
    </div>
  );
}
